import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, CheckCircle2, Mail } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  changeEmailSchema,
  type ChangeEmailForm,
} from '@/lib/schemas/profile';
import { useRequestEmailChange, type ProfileMe } from '@/lib/api/profile';
import { ApiError } from '@/lib/api';

/**
 * Раздел Email — текущий + форма запроса смены.
 *
 * Backend: `POST /profile/me/email/request` принимает new_email + current_password.
 * Возвращает 202 — реальный change происходит после клика по подтверждающей
 * ссылке в письме. UI показывает «письмо отправлено» и сворачивает форму.
 */
export function ProfileSectionEmail({ me }: { me: ProfileMe }) {
  const requestMut = useRequestEmailChange();
  const [showForm, setShowForm] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ChangeEmailForm>({
    resolver: zodResolver(changeEmailSchema),
    mode: 'onBlur',
    defaultValues: { new_email: '', current_password: '' },
  });

  const onSubmit = async (data: ChangeEmailForm) => {
    setServerError(null);
    try {
      await requestMut.mutateAsync(data);
      setSent(true);
      reset();
      setShowForm(false);
    } catch (err) {
      if (err instanceof ApiError) setServerError(err.detail);
      else setServerError('Сетевая ошибка');
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-sm">
        <span className="text-muted-foreground">Текущий email:</span>{' '}
        <span className="font-medium text-foreground break-all">{me.email}</span>
      </div>

      {sent && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            Письмо с подтверждением отправлено на новый адрес.
          </AlertDescription>
        </Alert>
      )}

      {!showForm ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setSent(false);
            setShowForm(true);
          }}
        >
          <Mail className="h-4 w-4 mr-2" />
          Сменить email
        </Button>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          {serverError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="profile-new-email">Новый email</Label>
            <Input
              id="profile-new-email"
              type="email"
              autoComplete="email"
              aria-invalid={!!errors.new_email}
              {...register('new_email')}
            />
            {errors.new_email && (
              <p className="text-destructive text-xs">{errors.new_email.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="profile-email-current-password">
              Текущий пароль
            </Label>
            <PasswordInput
              id="profile-email-current-password"
              autoComplete="current-password"
              aria-invalid={!!errors.current_password}
              {...register('current_password')}
            />
            {errors.current_password && (
              <p className="text-destructive text-xs">
                {errors.current_password.message}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Отправляем…' : 'Отправить письмо'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowForm(false);
                setServerError(null);
                reset();
              }}
            >
              Отмена
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
